// src/services/opportunityService.js
import {
  collection, doc, addDoc, updateDoc, getDoc,
  serverTimestamp, arrayUnion,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Write a notification to a user's own notifications (they always own these docs)
async function notify(userId, payload) {
  await addDoc(collection(db, 'notifications'), {
    userId,
    read: false,
    createdAt: serverTimestamp(),
    ...payload,
  });
}

// ── Collaborator submits a new opportunity ────────────────────────────
export async function submitOpportunity(collaboratorId, collaboratorName, data) {
  const ref = await addDoc(collection(db, 'opportunities'), {
    ...data,
    collaboratorId,
    collaboratorName,
    status:   'pending',
    featured: false,
    urgent:   false,
    comments: [],
    hasUnreadComments:       false,
    hasNewCollaboratorReply: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Confirm submission back to the collaborator via their own notifications
  await notify(collaboratorId, {
    type:             'opportunity_submitted',
    opportunityId:    ref.id,
    opportunityTitle: data.title,
    message:          `🕐 "${data.title}" submitted — pending admin review.`,
  });

  return ref.id;
}

// ── Admin: approve / reject / suspend ────────────────────────────────
// Admin has full Firestore write access — no permission issue here.
export async function moderateOpportunity(jobId, status, adminName) {
  const oppRef  = doc(db, 'opportunities', jobId);
  const oppSnap = await getDoc(oppRef);
  if (!oppSnap.exists()) throw new Error('Opportunity not found');
  const opp = oppSnap.data();

  const msgs = {
    live:      `✅ "${opp.title}" has been approved and is now live!`,
    rejected:  `❌ "${opp.title}" was not approved. Check admin comments for details.`,
    suspended: `⚠️ "${opp.title}" has been temporarily suspended.`,
    pending:   `🔄 "${opp.title}" has been moved back to pending review.`,
  };

  await updateDoc(oppRef, {
    status,
    updatedAt:   serverTimestamp(),
    moderatedBy: adminName,
    moderatedAt: serverTimestamp(),
  });

  await notify(opp.collaboratorId, {
    type:             'opportunity_status',
    opportunityId:    jobId,
    opportunityTitle: opp.title,
    status,
    message: msgs[status] || `Status changed to ${status}`,
  });
}

export async function toggleFeatured(jobId, featured) {
  await updateDoc(doc(db, 'opportunities', jobId), { featured, updatedAt: serverTimestamp() });
}

export async function toggleUrgent(jobId, urgent) {
  await updateDoc(doc(db, 'opportunities', jobId), { urgent, updatedAt: serverTimestamp() });
}

// ── Admin posts a comment ─────────────────────────────────────────────
// Admin writes comment to the opportunity doc + notifies the collaborator.
export async function addAdminComment(jobId, adminId, adminName, text) {
  const oppRef  = doc(db, 'opportunities', jobId);
  const oppSnap = await getDoc(oppRef);
  if (!oppSnap.exists()) throw new Error('Opportunity not found');
  const opp = oppSnap.data();

  const comment = {
    id:         `${Date.now()}_${adminId}`,
    authorId:   adminId,
    authorName: adminName,
    role:       'admin',
    text,
    createdAt:  new Date().toISOString(),
    read:       false,
  };

  await updateDoc(oppRef, {
    comments:          arrayUnion(comment),
    hasUnreadComments: true,
    updatedAt:         serverTimestamp(),
  });

  await notify(opp.collaboratorId, {
    type:             'opportunity_comment',
    opportunityId:    jobId,
    opportunityTitle: opp.title,
    message:          `💬 Admin commented on "${opp.title}"`,
    commentPreview:   text.length > 80 ? text.slice(0, 80) + '…' : text,
  });
}

// ── Collaborator replies to a comment ─────────────────────────────────
// ONLY updates the opportunity doc (which the collaborator owns).
// Does NOT write to adminNotifications — that collection requires admin access.
// The admin sees new replies via the hasNewCollaboratorReply flag on the doc.
export async function addCollaboratorComment(jobId, collaboratorId, collaboratorName, text) {
  const oppRef  = doc(db, 'opportunities', jobId);
  const oppSnap = await getDoc(oppRef);
  if (!oppSnap.exists()) throw new Error('Opportunity not found');

  const opp = oppSnap.data();
  if (opp.collaboratorId !== collaboratorId) {
    throw new Error('You are not the owner of this listing');
  }

  const comment = {
    id:         `${Date.now()}_${collaboratorId}`,
    authorId:   collaboratorId,
    authorName: collaboratorName,
    role:       'collaborator',
    text,
    createdAt:  new Date().toISOString(),
    read:       false,
  };

  // Write only to the opportunity doc — collaborator owns it so this is always allowed
  await updateDoc(oppRef, {
    comments:               arrayUnion(comment),
    hasNewCollaboratorReply: true,   // admin sees this as an unread reply badge
    updatedAt:              serverTimestamp(),
  });
}

// ── Clear unread flag when collaborator opens a thread ───────────────
export async function markCommentsRead(jobId) {
  await updateDoc(doc(db, 'opportunities', jobId), {
    hasUnreadComments: false,
  });
}
